import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../core/AppContext';

/**
 * LabVendingView — EEA Lab Vending platform (HARD-ISOLATED).
 *
 * Three layers in one view:
 *   1. Blueprint catalog (menu) — pick a blueprint to vend.
 *   2. Self-serve vend form — owner, duration (weeks), per-blueprint options
 *      (TrueFoundry env, Bedrock model, trace interval).
 *   3. Dashboard — all vends: owner, blueprint, status, weeks, expiry, cost,
 *      with lifecycle actions (Provision / Start / Stop / Destroy).
 *
 * Self-contained state + fetches; reads only `can` from the shared app context
 * for RBAC. Backed by /api/lab-blueprints, /api/lab-vends, /api/lab-costs.
 */
const API = '/api';
const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '18px', marginBottom: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' };
const btn = (bg, disabled) => ({ padding: '7px 14px', border: 'none', borderRadius: '4px', fontWeight: 'bold', fontSize: '12px', background: disabled ? '#cbd5e0' : bg, color: '#fff', cursor: disabled ? 'default' : 'pointer' });

const safe = (v) => { if (v == null) return ''; if (typeof v === 'string') return v; try { return JSON.stringify(v); } catch { return String(v); } };
const headers = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` });
const fmtDate = (sec) => sec ? new Date(Number(sec) * 1000).toLocaleDateString() : '—';
const statusColor = (s) => ({ ACTIVE: '#28a745', PROVISIONING: '#b7791f', REQUESTED: '#0073bb', STOPPED: '#718096', EXPIRED: '#b00020', DESTROYED: '#a0aec0' }[s] || '#4a5568');

export default function LabVendingView() {
  const { can } = useApp();
  const canUse = can('use_lab_vending');
  const canManage = can('manage_config');

  const [blueprints, setBlueprints] = useState([]);
  const [vends, setVends] = useState([]);
  const [costs, setCosts] = useState({});
  const [status, setStatus] = useState('');
  const [tab, setTab] = useState('catalog'); // 'catalog' | 'dashboard'

  // vend form state
  const [selectedBp, setSelectedBp] = useState(null);
  const [owner, setOwner] = useState('');
  const [weeks, setWeeks] = useState(2);
  const [opts, setOpts] = useState({});
  // Agent-code repo intent (Option A per-vend repo + Option C bring-your-own).
  const [agentRepoMode, setAgentRepoMode] = useState('template'); // 'template' | 'byo'
  const [agentRepoUrl, setAgentRepoUrl] = useState('');
  // TrueFoundry gateway model (feature D): route the agent through the TF
  // gateway so calls are traced. '' = Bedrock-direct (no trace).
  const [tfModels, setTfModels] = useState([]);     // live gateway model ids
  const [tfModel, setTfModel] = useState('');       // selected id ('' or '__custom__')
  const [tfModelCustom, setTfModelCustom] = useState('');
  // Action log modal (per-vend troubleshooting trail of provision/destroy/start/stop).
  const [logModal, setLogModal] = useState(null);   // { vendId, action, steps } | null
  // Live-status detail panel (expandable per vend row).
  const [expanded, setExpanded] = useState(null);   // vend_id currently expanded
  const [detail, setDetail] = useState(null);        // detail payload for it
  const [detailLoading, setDetailLoading] = useState(false);

  const loadBlueprints = useCallback(async () => {
    try {
      const r = await fetch(`${API}/lab-blueprints`, { headers: headers() });
      if (r.ok) { const d = await r.json(); setBlueprints(d.blueprints || []); }
      else if (r.status === 403) setStatus('You lack the use_lab_vending privilege.');
    } catch (e) { setStatus('Error loading blueprints.'); }
  }, []);

  const loadVends = useCallback(async () => {
    try {
      const r = await fetch(`${API}/lab-vends`, { headers: headers() });
      if (r.ok) { const d = await r.json(); setVends(d.vends || []); }
    } catch (e) { /* non-fatal */ }
  }, []);

  const loadCosts = useCallback(async () => {
    try {
      const r = await fetch(`${API}/lab-costs?days=30`, { headers: headers() });
      if (r.ok) { const d = await r.json(); setCosts(d.costs || {}); }
    } catch (e) { /* non-fatal */ }
  }, []);

  // Live-fetch the TrueFoundry gateway's callable models for the vend-form picker.
  const loadTfModels = useCallback(async () => {
    try {
      const r = await fetch(`${API}/lab-tf-models`, { headers: headers() });
      if (r.ok) { const d = await r.json(); setTfModels(d.models || []); }
    } catch (e) { /* non-fatal — picker still offers custom entry */ }
  }, []);

  // Load the live-status detail for one vend (pipeline jobs, agent health,
  // resources, TrueFoundry status, today's cost).
  const loadDetail = useCallback(async (vendId) => {
    setDetailLoading(true);
    try {
      const r = await fetch(`${API}/lab-vends/${vendId}/detail`, { headers: headers() });
      if (r.ok) { const d = await r.json(); setDetail(d); }
      else setDetail(null);
    } catch (e) { setDetail(null); }
    setDetailLoading(false);
  }, []);

  const toggleExpand = (vendId) => {
    if (expanded === vendId) { setExpanded(null); setDetail(null); return; }
    setExpanded(vendId); setDetail(null); loadDetail(vendId);
  };

  // Live 'Test gateway' probe: one real gateway call with the vend's tf_model.
  const [tfTest, setTfTest] = useState({});        // vendId -> probe result
  const [tfTesting, setTfTesting] = useState(null); // vendId currently probing
  const runTfTest = useCallback(async (vendId) => {
    setTfTesting(vendId);
    setTfTest(prev => ({ ...prev, [vendId]: null }));
    try {
      const r = await fetch(`${API}/lab-vends/${vendId}/tf-test`, { method: 'POST', headers: headers() });
      const d = await r.json().catch(() => ({ ok: false, error: 'bad response' }));
      setTfTest(prev => ({ ...prev, [vendId]: d }));
    } catch (e) {
      setTfTest(prev => ({ ...prev, [vendId]: { ok: false, error: String(e) } }));
    }
    setTfTesting(null);
  }, []);

  useEffect(() => { if (canUse) { loadBlueprints(); loadVends(); loadCosts(); loadTfModels(); } /* eslint-disable-next-line */ }, [canUse]);

  // Poll the dashboard while any vend is transitioning.
  useEffect(() => {
    if (tab !== 'dashboard') return;
    const transitioning = vends.some(v => v.status === 'PROVISIONING');
    const t = setInterval(() => {
      loadVends(); loadCosts();
      if (expanded) loadDetail(expanded);   // keep the open detail panel live
    }, transitioning ? 6000 : 30000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [tab, vends.map(v => v.status).join(',')]);

  const pickBlueprint = (bp) => {
    setSelectedBp(bp);
    setOpts({ ...(bp.defaults || {}) });
    setAgentRepoMode('template');
    setAgentRepoUrl('');
    setTfModel('');
    setTfModelCustom('');
    setStatus('');
  };

  const submitVend = async () => {
    if (!selectedBp) return;
    setStatus('Submitting vend request…');
    try {
      const r = await fetch(`${API}/lab-vends`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          owner: owner.trim(), blueprint_id: selectedBp.id, weeks: Number(weeks),
          options: {
            ...opts,
            agent_repo_mode: agentRepoMode,
            agent_repo_url: agentRepoMode === 'byo' ? agentRepoUrl.trim() : '',
            tf_model: tfModel === '__custom__' ? tfModelCustom.trim() : tfModel,
          },
        }),
      });
      const d = await r.json();
      if (d.status === 'SUCCESS') {
        setStatus(`Vend '${d.vend.vend_id}' requested for ${d.vend.weeks} week(s).`);
        setSelectedBp(null); setOwner('');
        loadVends(); setTab('dashboard');
      } else {
        setStatus(`Vend failed: ${safe(d.error || d.status)}`);
      }
    } catch (e) { setStatus('Error submitting vend.'); }
  };

  const act = async (vendId, action) => {
    setStatus(`${action}…`);
    const body = { action };
    // Destroy: ask whether to keep or delete the agent repo (default DELETE).
    if (action === 'destroy') {
      // OK = keep the repo; Cancel = delete it with the vend (the default).
      const keep = window.confirm(
        `Destroy lab ${vendId}.\n\nKeep the agent code repo?\n\n` +
        `OK = KEEP the repo (your code is preserved)\n` +
        `Cancel = DELETE the repo along with the lab (default)`
      );
      body.keep_repo = keep;
    }
    try {
      const r = await fetch(`${API}/lab-vends/${vendId}`, { method: 'POST', headers: headers(), body: JSON.stringify(body) });
      const d = await r.json();
      setStatus(d.status === 'TRIGGERED' ? `${action} triggered for ${vendId}.` : `${action}: ${safe(d.error || d.status)}`);
      // If the action didn't actually trigger a pipeline (stub/error), pop the
      // log so the failure + inputs are visible immediately.
      if (d.status !== 'TRIGGERED' && Array.isArray(d.action_log)) {
        setLogModal({ vendId, action, steps: d.action_log });
      }
      loadVends();
    } catch (e) { setStatus('Error running action.'); }
  };

  // Open the stored last-action log for a vend (persisted server-side).
  const viewLog = (v) => {
    let parsed = null;
    try { parsed = v.last_action_log ? JSON.parse(v.last_action_log) : null; } catch (e) { parsed = null; }
    setLogModal({ vendId: v.vend_id, action: parsed ? parsed.action : '', steps: parsed ? parsed.steps : [] });
  };

  const removeVend = async (vendId) => {
    if (!window.confirm(`Delete vend record ${vendId}? (Destroy its resources first.)`)) return;
    try {
      const r = await fetch(`${API}/lab-vends/${vendId}`, { method: 'DELETE', headers: headers() });
      const d = await r.json();
      setStatus(d.status === 'SUCCESS' ? `Vend ${vendId} deleted.` : `Delete failed: ${safe(d.error)}`);
      loadVends();
    } catch (e) { setStatus('Error deleting vend.'); }
  };

  if (!canUse) {
    return (
      <div style={{ ...card, marginTop: '10px', background: '#f8d7da', borderLeft: '4px solid #dc3545', color: '#721c24', fontSize: '13px' }}>
        You do not have the <code>use_lab_vending</code> privilege. Ask an admin to grant it in Admin → Users.
      </div>
    );
  }

  const tabBtn = (id, text) => (
    <button onClick={() => setTab(id)} style={{
      padding: '8px 18px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold',
      borderBottom: tab === id ? '3px solid #0073bb' : '3px solid transparent',
      background: 'transparent', color: tab === id ? '#0073bb' : '#718096',
    }}>{text}</button>
  );

  return (
    <div style={{ marginTop: '10px' }}>
      {/* Header */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: '15px' }}>
        <div style={{ fontSize: '38px' }}>🧪</div>
        <div style={{ flex: 1 }}>
          <h2 style={{ color: '#0f2027', margin: 0 }}>EEA Lab Vending Platform</h2>
          <p style={{ color: '#718096', margin: '4px 0 0 0', fontSize: '13px' }}>
            Self-serve provisioning of sandboxed AI lab blueprints — per-vend Terraform state, scheduling, auto-expiry, and cost tracking.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #e2e8f0', marginBottom: '14px' }}>
        {tabBtn('catalog', '📦 Blueprint Catalog')}
        {tabBtn('dashboard', `📊 Vended Labs (${vends.length})`)}
      </div>

      {/* CATALOG TAB */}
      {tab === 'catalog' && (
        <div>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
            {blueprints.map(bp => (
              <div key={bp.id} style={{ ...card, flex: '1 1 320px', marginBottom: 0, cursor: 'pointer', border: selectedBp && selectedBp.id === bp.id ? '2px solid #0073bb' : '1px solid #e2e8f0' }}
                onClick={() => pickBlueprint(bp)}>
                <h3 style={{ margin: '0 0 6px 0', color: '#0f2027', fontSize: '15px' }}>{safe(bp.name)}</h3>
                <p style={{ fontSize: '12px', color: '#718096', margin: '0 0 10px 0' }}>{safe(bp.summary)}</p>
                <div style={{ fontSize: '11px', color: '#a0aec0' }}>
                  ~${safe(bp.est_hourly_usd)}/hr · module: {safe(bp.tf_module)}
                </div>
                {bp.gitlab_repo && <div style={{ fontSize: '11px', color: '#0073bb', marginTop: '4px' }}>✎ editable in {safe(bp.gitlab_repo)}</div>}
              </div>
            ))}
            {blueprints.length === 0 && <p style={{ color: '#a0aec0', fontSize: '13px' }}>No blueprints available.</p>}
          </div>

          {/* Vend form (appears when a blueprint is selected) */}
          {selectedBp && (
            <div style={{ ...card, marginTop: '16px' }}>
              <h3 style={{ marginTop: 0, color: '#0f2027' }}>Vend: {safe(selectedBp.name)}</h3>
              <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  Owner (email / id)
                  <input value={owner} onChange={e => setOwner(e.target.value)} placeholder="you@dish.com"
                    style={{ padding: '7px', border: '1px solid #ccc', borderRadius: '4px', minWidth: '220px' }} />
                </label>
                <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  Duration (weeks)
                  <input type="number" min="1" max="52" value={weeks} onChange={e => setWeeks(e.target.value)}
                    style={{ padding: '7px', border: '1px solid #ccc', borderRadius: '4px', width: '100px' }} />
                </label>
                {/* Per-blueprint option selectors */}
                {Object.entries(selectedBp.options || {}).map(([key, choices]) => (
                  <label key={key} style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    {key}
                    <select value={opts[key] || ''} onChange={e => setOpts({ ...opts, [key]: e.target.value })}
                      style={{ padding: '7px', border: '1px solid #ccc', borderRadius: '4px', minWidth: '160px' }}>
                      {(choices || []).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                ))}
                {/* Agent-code repo: per-vend template repo, or bring-your-own. */}
                <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  Agent code
                  <select value={agentRepoMode} onChange={e => setAgentRepoMode(e.target.value)}
                    style={{ padding: '7px', border: '1px solid #ccc', borderRadius: '4px', minWidth: '220px' }}>
                    <option value="template">New repo from starter template</option>
                    <option value="byo">Use my existing repo</option>
                  </select>
                </label>
                {agentRepoMode === 'byo' && (
                  <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    Your repo URL (https)
                    <input value={agentRepoUrl} onChange={e => setAgentRepoUrl(e.target.value)}
                      placeholder="https://gitlab.com/you/your-agent.git"
                      style={{ padding: '7px', border: '1px solid #ccc', borderRadius: '4px', minWidth: '320px' }} />
                  </label>
                )}
                {/* TrueFoundry gateway model (feature D): route the agent through
                    the TF gateway so calls are traced. '' = Bedrock-direct. */}
                <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  TrueFoundry model (traced)
                  <select value={tfModel} onChange={e => setTfModel(e.target.value)}
                    style={{ padding: '7px', border: '1px solid #ccc', borderRadius: '4px', minWidth: '280px' }}>
                    <option value="">— none (Bedrock direct, no trace) —</option>
                    {tfModels.map(m => <option key={m} value={m}>{m}</option>)}
                    <option value="__custom__">— custom (type a model/VM id) —</option>
                  </select>
                </label>
                {tfModel === '__custom__' && (
                  <label style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    Custom TF model / VM id
                    <input value={tfModelCustom} onChange={e => setTfModelCustom(e.target.value)}
                      placeholder="group/virtual-model  or  account/model"
                      style={{ padding: '7px', border: '1px solid #ccc', borderRadius: '4px', minWidth: '280px' }} />
                  </label>
                )}
              </div>
              <p style={{ fontSize: '11px', color: '#718096', marginTop: '8px', marginBottom: 0 }}>
                {agentRepoMode === 'template'
                  ? 'A new GitLab repo is created from the agent-starter template; edit it and re-provision to deploy changes. It is deleted when you destroy the lab (unless you choose keep).'
                  : 'Your existing repo is deployed as-is. It must contain agent/handler.py. Your repo is never deleted by the lab.'}
              </p>
              <div style={{ marginTop: '14px', display: 'flex', gap: '10px' }}>
                <button onClick={submitVend} disabled={!owner.trim()} style={btn('#28a745', !owner.trim())}>Submit Vend Request</button>
                <button onClick={() => setSelectedBp(null)} style={{ ...btn('#fff'), color: '#4a5568', border: '1px solid #cbd5e0' }}>Cancel</button>
              </div>
              <p style={{ fontSize: '11px', color: '#a0aec0', marginTop: '10px', marginBottom: 0 }}>
                Submitting records the vend and renders its Terraform state key + commands. Provisioning runs from the Dashboard.
              </p>
            </div>
          )}
        </div>
      )}

      {/* DASHBOARD TAB */}
      {tab === 'dashboard' && (
        <div style={{ ...card }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3 style={{ margin: 0, color: '#0f2027' }}>Vended Labs</h3>
            <button onClick={() => { loadVends(); loadCosts(); }} style={{ ...btn('#fff'), color: '#4a5568', border: '1px solid #cbd5e0' }}>↻ Refresh</button>
          </div>
          {vends.length === 0 && <p style={{ color: '#a0aec0', fontSize: '13px' }}>No labs vended yet. Pick a blueprint in the Catalog tab.</p>}
          {vends.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f7fafc', textAlign: 'left' }}>
                    {['Vend', 'Blueprint', 'Owner', 'Status', 'Pipeline', 'Weeks', 'Expires', 'Cost (30d)', 'Actions'].map(h =>
                      <th key={h} style={{ padding: '8px' }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {vends.map(v => (
                  <React.Fragment key={v.vend_id}>
                    <tr style={{ borderBottom: '1px solid #edf2f7' }}>
                      <td style={{ padding: '8px', fontFamily: 'monospace' }}>
                        <span onClick={() => toggleExpand(v.vend_id)} style={{ cursor: 'pointer', color: '#0073bb' }}>
                          {expanded === v.vend_id ? '▾ ' : '▸ '}{safe(v.vend_id)}
                        </span>
                        {v.agent_repo_url
                          ? <div style={{ fontSize: '10px', marginTop: '2px' }}>
                              <a href={v.agent_repo_url.replace(/\.git$/, '')} target="_blank" rel="noopener noreferrer" style={{ color: '#0073bb' }}>
                                agent repo{v.agent_repo_mode === 'byo' ? ' (byo)' : ''}
                              </a>
                            </div>
                          : null}
                      </td>
                      <td style={{ padding: '8px' }}>{safe(v.blueprint_name || v.blueprint_id)}</td>
                      <td style={{ padding: '8px' }}>{safe(v.owner)}</td>
                      <td style={{ padding: '8px', fontWeight: 'bold', color: statusColor(v.status) }}>{safe(v.status)}</td>
                      <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '11px' }}>
                        {v.pipeline_url
                          ? <a href={v.pipeline_url} target="_blank" rel="noopener noreferrer" style={{ color: '#0073bb' }}>#{safe(v.pipeline_id || v.pipeline_url.split('/').pop())}</a>
                          : <span style={{ color: '#a0aec0' }}>—</span>}
                      </td>
                      <td style={{ padding: '8px' }}>{safe(v.weeks)}</td>
                      <td style={{ padding: '8px' }}>{fmtDate(v.expires_at)}</td>
                      <td style={{ padding: '8px' }}>${(costs[v.vend_id] != null ? costs[v.vend_id] : 0).toFixed ? (costs[v.vend_id] || 0).toFixed(2) : safe(costs[v.vend_id] || '0')}</td>
                      <td style={{ padding: '8px' }}>
                        {canManage ? (
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            <button onClick={() => act(v.vend_id, 'provision')} style={{ ...btn('#0073bb'), padding: '3px 8px', fontSize: '10px' }}>Provision</button>
                            <button onClick={() => act(v.vend_id, 'start')} style={{ ...btn('#28a745'), padding: '3px 8px', fontSize: '10px' }}>Start</button>
                            <button onClick={() => act(v.vend_id, 'stop')} style={{ ...btn('#b7791f'), padding: '3px 8px', fontSize: '10px' }}>Stop</button>
                            <button onClick={() => act(v.vend_id, 'destroy')} style={{ ...btn('#dc3545'), padding: '3px 8px', fontSize: '10px' }}>Destroy</button>
                            <button onClick={() => removeVend(v.vend_id)} style={{ ...btn('#a0aec0'), padding: '3px 8px', fontSize: '10px' }}>Delete</button>
                            <button title="Action log — steps, inputs, and errors from the last provision/destroy/start/stop" onClick={() => viewLog(v)} style={{ ...btn('#2d3748'), padding: '3px 8px', fontSize: '10px' }}>🗒 Log</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <span style={{ color: '#a0aec0' }}>view only</span>
                            <button title="Action log" onClick={() => viewLog(v)} style={{ ...btn('#2d3748'), padding: '3px 8px', fontSize: '10px' }}>🗒 Log</button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {expanded === v.vend_id && (
                      <tr style={{ background: '#f9fbfd' }}>
                        <td colSpan={9} style={{ padding: '12px 16px', borderBottom: '2px solid #e2e8f0' }}>
                          {detailLoading && !detail && <div style={{ fontSize: '12px', color: '#718096' }}>Loading live status…</div>}
                          {detail && detail.status === 'SUCCESS' && (
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                              {/* Tile: pipeline stage/job progress */}
                              <div style={{ ...card, flex: '1 1 240px', margin: 0, padding: '12px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#0f2027', marginBottom: '6px' }}>⛓ Pipeline progress</div>
                                {(detail.pipeline_jobs || []).length === 0
                                  ? <div style={{ fontSize: '11px', color: '#a0aec0' }}>No live pipeline (set the GitLab API token to see job progress).</div>
                                  : (detail.pipeline_jobs || []).map((j, i) => (
                                      <div key={i} style={{ fontSize: '11px', display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                                        <span>{safe(j.stage)} / {safe(j.name)}</span>
                                        <span style={{ fontWeight: 'bold', color: j.status === 'success' ? '#28a745' : j.status === 'failed' ? '#dc3545' : '#b7791f' }}>{safe(j.status)}</span>
                                      </div>
                                    ))}
                              </div>
                              {/* Tile: agent health */}
                              <div style={{ ...card, flex: '1 1 240px', margin: 0, padding: '12px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#0f2027', marginBottom: '6px' }}>🤖 Agent health</div>
                                {detail.agent_health && detail.agent_health.has_logs
                                  ? <div style={{ fontSize: '11px', color: '#4a5568' }}>
                                      <div>Last trace: {safe(detail.agent_health.last_trace_ts) || '—'}</div>
                                      <div style={{ marginTop: '4px' }}>Last reply:</div>
                                      <div style={{ fontStyle: 'italic', color: '#2d3748', maxHeight: '72px', overflow: 'auto' }}>{safe(detail.agent_health.last_reply) || '—'}</div>
                                      {detail.agent_health.last_error && <div style={{ color: '#dc3545', marginTop: '4px' }}>Error: {safe(detail.agent_health.last_error)}</div>}
                                    </div>
                                  : <div style={{ fontSize: '11px', color: '#a0aec0' }}>No agent logs yet (runs on the schedule; may take a tick).</div>}
                              </div>
                              {/* Tile: TrueFoundry status */}
                              <div style={{ ...card, flex: '1 1 240px', margin: 0, padding: '12px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#0f2027', marginBottom: '6px' }}>🔀 TrueFoundry</div>
                                {(() => {
                                  const tfs = detail.truefoundry || {};
                                  const st = tfs.state || (tfs.wired ? 'configured' : 'bedrock_direct');
                                  const meta = {
                                    routed:        { dot: '🟢', label: 'Routed through TrueFoundry', color: '#155724' },
                                    configured:    { dot: '🟡', label: 'Configured — not yet proven', color: '#9a5a1b' },
                                    bedrock_direct:{ dot: tfs.fell_back ? '🟠' : '⚪', label: tfs.fell_back ? 'Fell back to Bedrock-direct' : 'Bedrock-direct', color: tfs.fell_back ? '#c05621' : '#718096' },
                                  }[st] || { dot: '⚪', label: st, color: '#718096' };
                                  return (
                                    <div style={{ fontSize: '11px', color: '#4a5568' }}>
                                      <div style={{ fontWeight: 'bold', color: meta.color, marginBottom: '4px' }}>{meta.dot} {meta.label}</div>
                                      <div>Env: {safe(tfs.tf_env)}</div>
                                      {tfs.tf_model && <div>Model: <code>{safe(tfs.tf_model)}</code></div>}
                                      {tfs.proven && tfs.actual_model && <div>Served by: <code>{safe(tfs.actual_model)}</code></div>}
                                      {tfs.request_id && <div>Request: <code>{safe(tfs.request_id)}</code></div>}
                                      {tfs.note && <div style={{ color: '#718096', marginTop: '4px', fontStyle: 'italic' }}>{safe(tfs.note)}</div>}
                                      {tfs.trace_url && <div style={{ marginTop: '4px' }}><a href={tfs.trace_url} target="_blank" rel="noopener noreferrer" style={{ color: '#0073bb' }}>View traces →</a></div>}
                                      {/* Live 'Test gateway' probe — proves reachability + auth + callable model. */}
                                      <div style={{ marginTop: '8px', borderTop: '1px solid #edf2f7', paddingTop: '6px' }}>
                                        <button type="button" onClick={() => runTfTest(expanded)} disabled={tfTesting === expanded || !tfs.tf_model}
                                                title={tfs.tf_model ? 'Make one live gateway call with this vend\u2019s model' : 'No TrueFoundry model on this vend'}
                                                style={{ ...btn('#0073bb', tfTesting === expanded || !tfs.tf_model), padding: '5px 10px' }}>
                                          {tfTesting === expanded ? 'Testing…' : 'Test gateway'}
                                        </button>
                                        {tfTest[expanded] && (
                                          <div style={{ marginTop: '6px', fontSize: '11px', color: tfTest[expanded].ok ? '#155724' : '#b00020' }}>
                                            {tfTest[expanded].ok
                                              ? <>✓ Gateway OK{tfTest[expanded].latency_ms != null ? ` (${tfTest[expanded].latency_ms}ms)` : ''} · <code>{safe(tfTest[expanded].model)}</code>{tfTest[expanded].request_id ? <> · req <code>{safe(tfTest[expanded].request_id)}</code></> : null}{tfTest[expanded].reply ? <div style={{ color: '#4a5568', fontStyle: 'italic', marginTop: '2px' }}>“{safe(tfTest[expanded].reply)}”</div> : null}</>
                                              : <>✗ {safe(tfTest[expanded].error || tfTest[expanded].status)}</>}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                              {/* Tile: AWS resources + logs */}
                              <div style={{ ...card, flex: '1 1 240px', margin: 0, padding: '12px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#0f2027', marginBottom: '6px' }}>☁ AWS resources</div>
                                {detail.resources && (
                                  <div style={{ fontSize: '11px', color: '#4a5568' }}>
                                    <div>Lambda: <code>{safe(detail.resources.lambda_function)}</code></div>
                                    <div>Schedule: {safe(detail.resources.schedule)} ({safe(detail.resources.event_rule)})</div>
                                    <div style={{ marginTop: '4px' }}><a href={detail.resources.logs_url} target="_blank" rel="noopener noreferrer" style={{ color: '#0073bb' }}>CloudWatch Logs →</a></div>
                                  </div>
                                )}
                              </div>
                              {/* Tile: today's cost */}
                              <div style={{ ...card, flex: '1 1 160px', margin: 0, padding: '12px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#0f2027', marginBottom: '6px' }}>💲 Cost (today)</div>
                                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f2027' }}>${Number(detail.today_cost || 0).toFixed(2)}</div>
                                <div style={{ fontSize: '10px', color: '#a0aec0' }}>30-day: ${Number(costs[v.vend_id] || 0).toFixed(2)}</div>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p style={{ fontSize: '11px', color: '#a0aec0', marginTop: '10px', marginBottom: 0 }}>
            Cost is attributed via the <code>VendId</code> AWS cost-allocation tag (Cost Explorer, trailing 30 days).
            Lifecycle actions render the exact Terraform commands and record intent; a runner applies them.
          </p>
        </div>
      )}

      {/* Action log modal — step-by-step trail of the last lifecycle action,
          with inputs (secrets masked) and any errors, for troubleshooting. */}
      {logModal && (
        <div onClick={() => setLogModal(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '8px', padding: '20px', width: '680px', maxWidth: '92%', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ margin: 0, color: '#0f2027' }}>Action log — {safe(logModal.action) || '—'} · <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{safe(logModal.vendId)}</span></h3>
              <button onClick={() => setLogModal(null)} style={{ ...btn('#a0aec0'), padding: '4px 10px' }}>Close</button>
            </div>
            {(!logModal.steps || logModal.steps.length === 0)
              ? <p style={{ fontSize: '13px', color: '#a0aec0' }}>No action log recorded yet. Run Provision / Destroy / Start / Stop to generate one.</p>
              : (
                <ol style={{ margin: 0, paddingLeft: '18px', fontSize: '12px' }}>
                  {logModal.steps.map((s, i) => (
                    <li key={i} style={{ marginBottom: '8px' }}>
                      <span style={{ fontWeight: 'bold', color: s.ok === false ? '#dc3545' : '#28a745' }}>
                        {s.ok === false ? '✗' : '✓'} {safe(s.step)}
                      </span>
                      {s.detail && Object.keys(s.detail).length > 0 && (
                        <pre style={{ margin: '4px 0 0 0', padding: '8px', background: '#f7fafc', border: '1px solid #edf2f7', borderRadius: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#2d3748' }}>
                          {JSON.stringify(s.detail, null, 2)}
                        </pre>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            <p style={{ fontSize: '10px', color: '#a0aec0', marginTop: '10px', marginBottom: 0 }}>
              Secrets (tokens) are masked as <code>set(len=…, …last4)</code> or <code>(empty)</code> — a <code>(empty)</code> token is usually the cause of a stub/failed trigger.
            </p>
          </div>
        </div>
      )}

      {status && <p style={{ fontSize: '12px', color: '#555', marginTop: '10px' }}>{safe(status)}</p>}
    </div>
  );
}
